import { Grid } from "@material-ui/core";
import { Rotate90DegreesCcw, StayCurrentLandscape, StayCurrentPortrait } from "@material-ui/icons";
import { DateTime } from "luxon";
import React, { useContext, useEffect } from "react";
import { connect, ConnectedProps } from "react-redux";
import ApplicationInsightsContext from "../../Shared/Components/ApplicationInsightsContext";
import { ExecutionType } from "../../Shared/GeneratedTypes";
import {
    acceptPrivacyStatement,
    checkQueuePlacement,
    clearPolicyHolderPhotoUrlLocally,
    enterQueue,
    getAccessInfoForQuickCode,
    getActiveMentee,
    getActiveObservers,
    getScheduleSlots,
    getSessionInfo,
    heartbeatPing,
    optOutOfPortaitPhoto,
    saveAudioRating,
    saveFeedback,
    saveOverallRating,
    saveVideoRating,
    setCurrentPosition,
    setPolicyHolderPhoto,
    setScheduleDateTime,
    updateCulture
} from "../redux/actions/currentClientActions";
import { enqueueSnackbar } from "../../Shared/Redux/Actions/notificationActions";
import "../styles/forcePortrait.css";
import { ApplicationData, ClientPhase } from "../types";
import Android from "./DeviceCheck/Android";
import IOS from "./DeviceCheck/iOS";
import AndroidDenied from "./Phases/AndroidDenied";
import CookieDisclosure from "./Phases/CookieDisclosure";
import FinalSurvey from "./Phases/FinalSurvey";
import IOSDenied from "./Phases/IOSDenied";
import MeetYourMentor from "./Phases/MeetYourMentor";
import MenteeAlreadyLoggedIn from "./Phases/MenteeAlreadyLoggedIn";
import Permissions from "./Phases/Permissions";
import PortraitPhoto from "./Phases/PortraitPhoto";
import Privacy from "./Phases/Privacy";
import ProvideFeedback from "./Phases/ProvideFeedback";
import ScheduleConfirmation from "./Phases/ScheduleConfirmation";
import Scheduling from "./Phases/Scheduling";
import SessionEnded from "./Phases/SessionEnded";
import WaitingQueue from "./Phases/WaitingQueue";
import Welcome from "./Phases/Welcome";
import WhatToExpect from "./Phases/WhatToExpect";

const mapState = function ({
    localizations,
    currentPhase,
    order,
    mentor,
    apiCallsInProgress,
    numberInQueue,
    estimatedStart,
    accessInfo,
    permissions,
    currentPosition,
    scheduleSlots,
    activeMentee,
}: ApplicationData) {
    return {
        localizations,
        currentPhase,
        order,
        apiCallsInProgress,
        mentor,
        numberInQueue,
        estimatedStart,
        accessInfo,
        permissions,
        currentPosition,
        scheduleSlots,
        deviceInfo: order.clientInfo?.deviceInfo,
        activeMentee,
    };
};

const mapDispatch = {
    getSessionInfo,
    getActiveObservers,
    heartbeatPing,
    setPolicyHolderPhoto,
    clearPolicyHolderPhotoUrlLocally,
    enterQueue,
    checkQueuePlacement,
    getAccessInfoForQuickCode,
    setCurrentPosition,
    acceptPrivacyStatement,
    setScheduleDateTime,
    enqueueSnackbar,
    saveAudioRating,
    saveFeedback,
    saveOverallRating,
    saveVideoRating,
    getScheduleSlots,
    updateCulture,
    getActiveMentee,
    optOutOfPortaitPhoto,
};
const connector = connect(mapState, mapDispatch);
type PropsFromRedux = ConnectedProps<typeof connector>;
type PropTypes = PropsFromRedux & {
    phase: ClientPhase;
    setPhase: (phase: ClientPhase) => void;
};

function IntroPhases({
    order,
    mentor,
    numberInQueue,
    setPolicyHolderPhoto,
    apiCallsInProgress,
    enterQueue,
    checkQueuePlacement,
    accessInfo,
    phase,
    setPhase,
    localizations,
    acceptPrivacyStatement,
    setScheduleDateTime,
    saveAudioRating,
    saveFeedback,
    saveOverallRating,
    saveVideoRating,
    getScheduleSlots,
    scheduleSlots,
    updateCulture,
    deviceInfo,
    activeMentee,
    optOutOfPortaitPhoto,
}: PropTypes) {
    useEffect(() => {
        if (order.executionType === ExecutionType.Scheduled && !order.scheduledDate) {
            getScheduleSlots(
                order.customer.preventSameDayScheduling
                    ? DateTime.local().startOf("day").plus({ days: 1 })
                    : DateTime.local(),
                DateTime.local().plus({ days: 30 })
            );
        }
    }, []);

    const appInsights = useContext(ApplicationInsightsContext);
    useEffect(() => {
        if (appInsights != null) {
            appInsights.addTelemetryInitializer((envelope) => {
                //They did not explain this very well in docs.  So I'll do both and try to see if either is better
                //https://github.com/microsoft/ApplicationInsights-JS#example-setting-cloud-role-name
                envelope.tags!["GuideStreamId"] = order.id;
                envelope.data!.GuideStreamId = order.id;
            });
        }
    }, []);

    useEffect(() => {
        if (appInsights != null) {
            appInsights.trackEvent({ name: `CLIENT-PHASE-${phase}-${ClientPhase[phase]}` });
        }
    }, [phase]);

    //Handle shifting some phases
    useEffect(() => {
        //Jump to permissions if already accepted privacy.  (that phase will skip if already have OK'd on chrome)
        if (
            phase === ClientPhase.Welcome &&
            order.executionType !== ExecutionType.InternalCommunication &&
            order.acceptedPrivacyStatement
        ) {
            const scheduledSession =
                order.executionType === ExecutionType.ExternallyScheduled ||
                order.executionType === ExecutionType.Scheduled;

            if (scheduledSession) {
                if (!order.scheduledDate) {
                    setPhase(ClientPhase.Scheduling);
                } else if ((order.scheduledDate as DateTime).diff(DateTime.local(), "hours").hours <= 1) {
                    setPhase(ClientPhase.WhatToExpect);
                } else {
                    setPhase(ClientPhase.ScheduleConfirmation);
                }
            } else {
                setPhase(ClientPhase.WhatToExpect);
            }
        }

        //Internal Jump to queue?
        if (phase === ClientPhase.Welcome && order.executionType === ExecutionType.InternalCommunication) {
            setPhase(ClientPhase.Queue);
        }

        if (phase == ClientPhase.Scheduling) {
            if (!!order.scheduledDate) {
                setPhase(ClientPhase.ScheduleConfirmation);
            }
        }
    }, [accessInfo, order]);

    function selectPhase(phase: ClientPhase) {
        if (deviceInfo != null) {
            if (
                (deviceInfo.os?.name === "iOS" || deviceInfo.os?.name === "Mac") &&
                deviceInfo.client?.name !== "Safari" &&
                deviceInfo.client?.name !== "Mobile Safari"
            ) {
                return <IOS order={order} quickCode={order.quickCode!} localizations={localizations}></IOS>;
            } else if (
                deviceInfo.os?.name?.toLowerCase() === "android" &&
                deviceInfo.client?.name?.toLowerCase()?.indexOf("chrome") === -1
            ) {
                return <Android order={order} quickCode={order.quickCode!} localizations={localizations}></Android>;
            }
        }

        const scheduledSession =
            order.executionType === ExecutionType.ExternallyScheduled ||
            order.executionType === ExecutionType.Scheduled;

        if (activeMentee) {
            setPhase(ClientPhase.MenteeAlreadyLoggedIn);
        }

        switch (phase) {
            default:
            case ClientPhase.Welcome: {
                return (
                    <Welcome
                        localizations={localizations}
                        next={() => setPhase(ClientPhase.Privacy)}
                        order={order}
                        apiCallsInProgress={apiCallsInProgress}
                        updateCulture={updateCulture}
                        viewCookieDisclosure={() => setPhase(ClientPhase.CookieDisclosure)}
                        device={deviceInfo}
                    ></Welcome>
                );
            }
            case ClientPhase.Privacy: {
                return (
                    <Privacy
                        next={() => {
                            if (!order.acceptedPrivacyStatement) {
                                acceptPrivacyStatement();
                            }

                            if (scheduledSession) {
                                if (!order.scheduledDate) {
                                    setPhase(ClientPhase.Scheduling);
                                } else {
                                    setPhase(ClientPhase.ScheduleConfirmation);
                                }
                            } else {
                                setPhase(ClientPhase.WhatToExpect);
                            }
                        }}
                        isAccepted={!!order.acceptedPrivacyStatement}
                        order={order}
                        localizations={localizations}
                    ></Privacy>
                );
            }
            case ClientPhase.WhatToExpect: {
                return (
                    <WhatToExpect
                        next={() => setPhase(ClientPhase.Permissions)}
                        localizations={localizations}
                    ></WhatToExpect>
                );
            }
            case ClientPhase.Permissions: {
                return (
                    <Permissions
                        nextPhase={() => {
                            if (order.contact.photoId || order.contact.optedOutOfPhoto) {
                                setPhase(ClientPhase.Queue);
                            } else {
                                setPhase(ClientPhase.Photo);
                            }
                        }}
                        deniedAccess={() => setPhase(ClientPhase.AccessDenied)}
                        localizations={localizations}
                    ></Permissions>
                );
            }
            case ClientPhase.AccessDenied: {
                if (deviceInfo?.os?.name?.toLowerCase() === "ios") {
                    return (
                        <IOSDenied
                            localizations={localizations}
                            images={order.imageLocalizations.filter((x) => x.deviceType == 1)}
                        />
                    );
                } else {
                    return (
                        <AndroidDenied
                            localizations={localizations}
                            images={order.imageLocalizations.filter((x) => x.deviceType == 0)}
                        />
                    );
                }
            }
            case ClientPhase.Photo: {
                return (
                    <PortraitPhoto
                        photo={order.contact.photoUrl}
                        localizations={localizations}
                        nextPhase={() => {
                            setPhase(ClientPhase.Queue);
                        }}
                        photoSavingApiCallCount={apiCallsInProgress}
                        photoTaken={(photo) => setPolicyHolderPhoto(photo)}
                        optOutOfPhoto={() => {
                            optOutOfPortaitPhoto();
                            setPhase(ClientPhase.Queue);
                        }}
                    ></PortraitPhoto>
                );
            }
            case ClientPhase.Scheduling: {
                return (
                    <Scheduling
                        scheduleSlots={scheduleSlots}
                        getScheduleSlots={getScheduleSlots}
                        scheduledDate={order.scheduledDate ? (order.scheduledDate as DateTime) : null}
                        setSchedule={(time) => {
                            setScheduleDateTime(time);
                        }}
                        localizations={localizations}
                        preventSameDayScheduling={order.customer.preventSameDayScheduling}
                        order={order}
                    ></Scheduling>
                );
            }
            case ClientPhase.ScheduleConfirmation: {
                return (
                    <ScheduleConfirmation
                        updateSchedule={() => setPhase(ClientPhase.Scheduling)}
                        next={() => setPhase(ClientPhase.WhatToExpect)}
                        order={order}
                        localizations={localizations}
                    ></ScheduleConfirmation>
                );
            }
            case ClientPhase.Queue: {
                return (
                    <WaitingQueue
                        placeInLine={numberInQueue}
                        customerPhotoUrl={order.logoPhotoUrl!}
                        order={order}
                        checkQueuePlacement={() => {
                            checkQueuePlacement();
                        }}
                        enterQueue={() => enterQueue()}
                        meetMentor={() => setPhase(ClientPhase.MeetYourMentor)}
                        changeSchedule={() => setPhase(ClientPhase.Scheduling)}
                        localizations={localizations}
                    ></WaitingQueue>
                );
            }
            case ClientPhase.MeetYourMentor: {
                return (
                    <MeetYourMentor
                        order={order}
                        mentor={mentor!}
                        beginSession={() => {
                            setPhase(ClientPhase.GuideStreamOnline);
                        }}
                        localizations={localizations}
                    ></MeetYourMentor>
                );
            }
            case ClientPhase.SessionEnded: {
                return (
                    <SessionEnded
                        order={order}
                        mentor={mentor!}
                        completeSurvey={() => setPhase(ClientPhase.Review)}
                        localizations={localizations}
                    ></SessionEnded>
                );
            }
            case ClientPhase.Review: {
                return (
                    <FinalSurvey
                        provideFeedback={() => setPhase(ClientPhase.ProvideFeedback)}
                        submitVideoRating={saveVideoRating}
                        submitAudioRating={saveAudioRating}
                        submitOverallRating={saveOverallRating}
                        order={order}
                        localizations={localizations}
                    ></FinalSurvey>
                );
            }
            case ClientPhase.ProvideFeedback: {
                return (
                    <ProvideFeedback
                        provideFeedback={(message) => {
                            setPhase(ClientPhase.Review);
                            saveFeedback(message);
                        }}
                        order={order}
                        localizations={localizations}
                    ></ProvideFeedback>
                );
            }
            case ClientPhase.CookieDisclosure: {
                return (
                    <CookieDisclosure
                        back={() => setPhase(ClientPhase.Welcome)}
                        order={order}
                        localizations={localizations}
                    ></CookieDisclosure>
                );
            }
            case ClientPhase.MenteeAlreadyLoggedIn: {
                return <MenteeAlreadyLoggedIn localizations={localizations}></MenteeAlreadyLoggedIn>;
            }
        }
    }
    return (
        <>
            <div className="introPhaseContainer">
                <Grid
                    style={{ height: "100%", margin: "auto" }}
                    lg={4}
                    md={8}
                    sm={12}
                    container
                    direction="column"
                    alignItems="stretch"
                >
                    {selectPhase(phase)}
                </Grid>
            </div>
            <div className="showInLandscape">
                <span>
                    {localizations.useDeviceInPortraitMode}
                    <br />
                    <span style={{ fontSize: "60px" }}>
                        <StayCurrentLandscape fontSize="inherit" style={{ marginRight: 16 }} />
                        <Rotate90DegreesCcw fontSize="inherit" style={{ marginRight: 16 }} />
                        <StayCurrentPortrait fontSize="inherit" style={{ marginRight: 16 }} />
                    </span>
                </span>
            </div>
        </>
    );
}

export default connector(IntroPhases);
